<template>
  <div class="pa-2 accounting-entry-import">
    <block v-if="loading"></block>

    <v-card flat class="pa-4 mb-4">
      <div class="d-flex flex-wrap justify-space-between align-center">
        <div>
          <h2 class="mb-1">Importacion de partidas</h2>
        </div>

        <div class="d-flex flex-wrap justify-end">
          <v-btn
            small
            color="btn-save"
            class="mr-2 mb-2"
            :disabled="loading || selectedValidEntries.length === 0"
            @click="importSelectedEntries"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
          <v-btn
            small
            color="btnclean"
            class="mr-2 mb-2"
            :disabled="loading || (!entries.length && !importResults.length)"
            @click="clearImport"
          >
            <v-icon left>mdi-broom</v-icon>
            Limpiar
          </v-btn>
          <v-btn small  class="mr-2 mb-2 btn-save" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Plantilla
          </v-btn>
          <v-btn small color="primary" class="mb-2" @click="triggerFileInput">
            <v-icon left>mdi-file-excel</v-icon>
            Cargar Excel
          </v-btn>
          <input
            ref="excelInput"
            type="file"
            accept=".xlsx,.xls"
            class="d-none"
            @change="handleFileChange"
          />
        </div>
      </div>
    </v-card>

    <v-card v-if="entries.length" flat class="pa-4 mb-4">
      <div class="d-flex flex-wrap justify-space-between align-center">
        <div class="mb-2">
          <strong>{{ entries.length }}</strong> partidas cargadas.
          <strong>{{ selectedValidEntries.length }}</strong> seleccionadas y validas para importar.
        </div>
        <div class="mb-2">
          <v-btn small class="mr-2" @click="selectAllEntries">
            Seleccionar todas
          </v-btn>
          <v-btn small @click="unselectAllEntries">
            Deseleccionar todas
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-alert v-if="importResults.length" dense text type="info" class="mb-4">
      <div class="font-weight-bold mb-2">Resultado de importacion</div>
      <div v-for="result in importResults" :key="result.localId" class="result-line">
        Partida #{{ result.numero }}:
        <span :class="result.success ? 'success--text' : 'error--text'">
          {{ result.success ? 'Creada correctamente' : result.message }}
        </span>
      </div>
    </v-alert>

    <v-alert v-if="!entries.length" dense text type="info">
      Descargue la plantilla o cargue un archivo Excel.
    </v-alert>

    <v-card
      v-for="(entry, entryIndex) in entries"
      :key="entry.localId"
      outlined
      class="entry-card mb-4"
      :class="{
        'entry-card-error': !isEntryValid(entry),
        'entry-card-warning': isEntryValid(entry) && !isEntryBalanced(entry),
        'entry-card-ready': isEntryValid(entry) && isEntryBalanced(entry)
      }"
    >
      <v-card-title class="entry-card-title">
        <v-checkbox
          v-model="entry.selected"
          class="ma-0 mr-3"
          hide-details
          :disabled="entry.saved || !isEntryValid(entry)"
        ></v-checkbox>
        <div class="entry-title-text">
          Partida #{{ entry.numero || entryIndex + 1 }} - {{ entry.fecha || 'Sin fecha' }} - {{ getTypeLabel(entry) }} - {{ entry.conceptoGeneral || 'Sin concepto' }}
        </div>
        <v-spacer></v-spacer>
        <v-chip small :color="getEntryStatusColor(entry)" text-color="white">
          {{ getEntryStatusLabel(entry) }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="entry.fecha"
              label="Fecha"
              type="date"
              outlined
              dense
              :error="!entry.fecha"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="entry.tipoId"
              :items="entryTypes"
              :item-text="typeItemText"
              item-value="id_accounting_entry_type"
              label="Tipo"
              outlined
              dense
              clearable
              :error="!entry.tipoId"
              hide-details="auto"
              @change="syncEntryTypeCode(entry)"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="entry.conceptoGeneral"
              label="Concepto general"
              outlined
              dense
              :error="!entry.conceptoGeneral"
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="entry-lines-wrapper mt-3">
          <table class="entry-lines-table">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Nombre</th>
                <th>Concepto</th>
                <th>Debe</th>
                <th>Haber</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, lineIndex) in entry.detalle" :key="line.localId">
                <td class="account-cell">
                  <div class="d-flex align-center">
                    <v-text-field
                      v-model="line.cuenta"
                      dense
                      outlined
                      hide-details
                      class="line-input"
                      :error="isLineAccountInvalid(line)"
                      @blur="resolveLineAccount(line)"
                    ></v-text-field>
                    <v-btn icon small class="ml-1" @click="openAccountDialog(entryIndex, lineIndex)">
                      <v-icon>mdi-magnify</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <v-text-field
                    v-model="line.nombre"
                    dense
                    outlined
                    hide-details
                    class="line-input"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="line.concepto"
                    dense
                    outlined
                    hide-details
                    class="line-input"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="line.debe"
                    dense
                    outlined
                    hide-details
                    type="number"
                    min="0"
                    class="amount-input"
                    :error="isLineAmountInvalid(line) || isLineDoubleAmountInvalid(line)"
                    @blur="formatLineAmount(line, 'debe')"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="line.haber"
                    dense
                    outlined
                    hide-details
                    type="number"
                    min="0"
                    class="amount-input"
                    :error="isLineAmountInvalid(line) || isLineDoubleAmountInvalid(line)"
                    @blur="formatLineAmount(line, 'haber')"
                  ></v-text-field>
                </td>
                <td class="text-center">
                  <v-btn icon small color="error" @click="removeLine(entry, lineIndex)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-3">
          <v-btn small color="btn-save" @click="addLine(entry)">
            <v-icon left>mdi-plus</v-icon>
            Agregar cuenta
          </v-btn>
        </div>

        <v-row dense class="mt-3">
          <v-col cols="12" md="3">
            <strong>Total debe:</strong> {{ currency }}{{ formatMoney(getEntryTotals(entry).totalDebe) }}
          </v-col>
          <v-col cols="12" md="3">
            <strong>Total haber:</strong> {{ currency }}{{ formatMoney(getEntryTotals(entry).totalHaber) }}
          </v-col>
          <v-col cols="12" md="3">
            <strong :class="isEntryBalanced(entry) ? '' : 'error--text'">
              Diferencia: {{ currency }}{{ formatMoney(getEntryTotals(entry).diferencia) }}
            </strong>
          </v-col>
          <v-col cols="12" md="3">
            <span v-if="getEntryErrors(entry).length" class="error--text">
              {{ getEntryErrors(entry)[0] }}
            </span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="entries.length" flat class="pa-4 text-right">
      <v-btn color="btn-save" :disabled="loading || selectedValidEntries.length === 0" @click="importSelectedEntries">
        <v-icon left>mdi-content-save</v-icon>
        Guardar
      </v-btn>
    </v-card>

    <v-dialog v-model="showAccountDialog" width="90%">
      <v-card style="height: 80vh !important; overflow-y: auto;">
        <AccountingItemForm
          :win="win"
          :key="accountDialogKey"
          @sendSelection="receiveAccountSelection"
          @closeDialog="showAccountDialog = false"
        />
      </v-card>
    </v-dialog>

    <alerts
      v-if="alert.show"
      :properties="alert"
      @close_alert="closeAlert"
      @accept_alert="acceptAlert"
    ></alerts>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import Alerts from "@/components/Alerts";
import Block from "@/components/Block";
import AccountingItemForm from "./AccountingItemForm.vue";

const TEMPLATE_HEADERS = ["Numero", "Fecha", "Tipo", "Cuenta", "Nombre", "Concepto", "Debe", "Haber"];

export default {
  name: "AccountingEntryImport",
  props: ["win"],
  components: {
    Alerts,
    Block,
    AccountingItemForm,
  },
  data() {
    return {
      loading: false,
      entries: [],
      entryTypes: [],
      accounts: [],
      detailTypes: [],
      importResults: [],
      showAccountDialog: false,
      accountDialogKey: 0,
      accountTarget: null,
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
      currency: (JSON.parse(localStorage.getItem("branch") || "{}").Currency) || "$",
    };
  },
  computed: {
    selectedValidEntries() {
      return this.entries.filter((entry) => entry.selected && !entry.saved && this.isEntryValid(entry));
    },
  },
  async mounted() {
    await this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        this.loading = true;
        const [typesResponse, accountsResponse, detailTypesResponse] = await Promise.all([
          this.$API.branchaccountcatalog.getAcounteringType(),
          this.$API.branchaccountcatalog.getActiveBranchAccountCatalog(),
          this.$API.accountingentrydetailtype.getAccountingEntryDetailTypes(),
        ]);

        this.entryTypes = typesResponse.data || [];
        this.accounts = accountsResponse.data || [];
        this.detailTypes = detailTypesResponse.data || [];
      } catch (error) {
        this.showAlert("danger", "Error", this.getErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    triggerFileInput() {
      this.$refs.excelInput.click();
    },
    clearImport() {
      this.entries = [];
      this.importResults = [];
      this.accountTarget = null;
      this.showAccountDialog = false;

      if (this.$refs.excelInput) {
        this.$refs.excelInput.value = "";
      }
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (readerEvent) => {
        try {
          const data = new Uint8Array(readerEvent.target.result);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
          this.entries = this.parseWorksheetRows(rows);
          this.importResults = [];

          if (!this.entries.length) {
            this.showAlert("warning", "Sin partidas", "No se encontraron partidas para importar en el archivo.");
            return;
          }

          this.showAlert("success", "Archivo cargado", `Se cargaron ${this.entries.length} partidas para revision.`);
        } catch (error) {
          this.showAlert("danger", "Error", `Ocurrio un error al procesar el archivo: ${this.getErrorMessage(error)}`);
        }
      };

      reader.readAsArrayBuffer(file);
      event.target.value = "";
    },
    parseWorksheetRows(rows) {
      if (!rows || rows.length < 2) return [];

      const headerRowIndex = rows.findIndex((row) => this.hasTemplateHeaders(row));
      if (headerRowIndex < 0) {
        throw new Error("El archivo no contiene las columnas requeridas: Numero, Fecha, Tipo, Cuenta, Nombre, Concepto, Debe y Haber.");
      }

      const headerMap = this.buildHeaderMap(rows[headerRowIndex]);
      const entries = [];
      let currentEntry = null;

      rows.slice(headerRowIndex + 1).forEach((row, index) => {
        if (this.isEmptyRow(row)) return;

        const parsedRow = this.rowToObject(row, headerMap);
        const hasNumber = this.hasValue(parsedRow.Numero);
        const hasAccount = this.hasValue(parsedRow.Cuenta);

        if (hasNumber && !hasAccount) {
          currentEntry = this.createEntryFromRow(parsedRow, entries.length, index);
          entries.push(currentEntry);
          return;
        }

        if (!currentEntry && hasAccount) {
          currentEntry = this.createEntryFromRow({ Numero: "Sin numero", Concepto: "Partida importada" }, entries.length, index);
          entries.push(currentEntry);
        }

        if (currentEntry && hasAccount) {
          currentEntry.detalle.push(this.createLineFromRow(parsedRow, currentEntry.detalle.length));
        }
      });

      return entries;
    },
    hasTemplateHeaders(row) {
      const normalized = row.map((value) => this.normalizeHeader(value));
      return TEMPLATE_HEADERS.every((header) => normalized.includes(this.normalizeHeader(header)));
    },
    buildHeaderMap(row) {
      return row.reduce((map, value, index) => {
        const normalized = this.normalizeHeader(value);
        const header = TEMPLATE_HEADERS.find((item) => this.normalizeHeader(item) === normalized);
        if (header) map[header] = index;
        return map;
      }, {});
    },
    normalizeHeader(value) {
      return (value || "").toString().trim().toLowerCase();
    },
    rowToObject(row, headerMap) {
      return TEMPLATE_HEADERS.reduce((obj, header) => {
        obj[header] = row[headerMap[header]];
        return obj;
      }, {});
    },
    createEntryFromRow(row, entryIndex, rowIndex) {
      const typeCode = this.cleanValue(row.Tipo).toUpperCase();
      const type = this.findEntryTypeByCode(typeCode);

      return {
        localId: `entry-${Date.now()}-${entryIndex}-${rowIndex}`,
        selected: true,
        saved: false,
        numero: this.cleanValue(row.Numero),
        fecha: this.parseExcelDate(row.Fecha),
        tipoCode: typeCode,
        tipoId: type ? type.id_accounting_entry_type : null,
        conceptoGeneral: this.cleanValue(row.Concepto),
        detalle: [],
      };
    },
    createLineFromRow(row, lineIndex) {
      const cuenta = this.cleanValue(row.Cuenta);
      const account = this.findAccountByCode(cuenta);

      return {
        localId: `line-${Date.now()}-${lineIndex}-${Math.random()}`,
        idAccount: account ? account.id_account : null,
        cuenta,
        nombre: this.cleanValue(row.Nombre) || (account ? account.account_description : ""),
        concepto: this.cleanValue(row.Concepto),
        debe: this.toAmount(row.Debe),
        haber: this.toAmount(row.Haber),
      };
    },
    hasValue(value) {
      return value !== null && value !== undefined && value.toString().trim() !== "";
    },
    isEmptyRow(row) {
      return !row.some((value) => this.hasValue(value));
    },
    cleanValue(value) {
      if (value === null || value === undefined) return "";
      return value.toString().trim();
    },
    toAmount(value) {
      if (value === null || value === undefined || value === "") return "";
      const parsed = parseFloat(value.toString().replace(/,/g, "").trim());
      return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
    },
    parseExcelDate(value) {
      if (!value) return "";

      if (value instanceof Date && !isNaN(value.getTime())) {
        return this.formatDateInput(value);
      }

      if (typeof value === "number") {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (parsed) {
          return `${parsed.y}-${this.pad(parsed.m)}-${this.pad(parsed.d)}`;
        }
      }

      const cleanDate = value.toString().trim();
      const isoMatch = cleanDate.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (isoMatch) return `${isoMatch[1]}-${this.pad(isoMatch[2])}-${this.pad(isoMatch[3])}`;

      const localMatch = cleanDate.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
      if (localMatch) return `${localMatch[3]}-${this.pad(localMatch[2])}-${this.pad(localMatch[1])}`;

      return cleanDate;
    },
    formatDateInput(date) {
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
    },
    pad(value) {
      return value.toString().padStart(2, "0");
    },
    findEntryTypeByCode(code) {
      return this.entryTypes.find((type) => (type.accounting_entry_code || "").toString().toUpperCase() === code);
    },
    findAccountByCode(accountCode) {
      return this.accounts.find((account) => account.account_code === accountCode && account.SelectedItem);
    },
    resolveLineAccount(line) {
      const account = this.findAccountByCode(line.cuenta);
      line.idAccount = account ? account.id_account : null;

      if (account) {
        line.cuenta = account.account_code;
        line.nombre = account.account_description;
      }
    },
    typeItemText(item) {
      if (!item) return "";
      return `${item.accounting_entry_code} - ${item.accounting_entry_name}`;
    },
    syncEntryTypeCode(entry) {
      const type = this.entryTypes.find((item) => item.id_accounting_entry_type === entry.tipoId);
      entry.tipoCode = type ? type.accounting_entry_code : "";
    },
    getTypeCode(entry) {
      const type = this.entryTypes.find((item) => item.id_accounting_entry_type === entry.tipoId);
      return type ? type.accounting_entry_code : entry.tipoCode;
    },
    getTypeLabel(entry) {
      const type = this.entryTypes.find((item) => item.id_accounting_entry_type === entry.tipoId);
      if (!type) return entry.tipoCode || "Sin tipo";

      return `${type.accounting_entry_code} - ${type.accounting_entry_name}`;
    },
    addLine(entry) {
      entry.detalle.push({
        localId: `line-${Date.now()}-${entry.detalle.length}`,
        idAccount: null,
        cuenta: "",
        nombre: "",
        concepto: entry.conceptoGeneral || "",
        debe: "",
        haber: "",
      });
    },
    removeLine(entry, lineIndex) {
      entry.detalle.splice(lineIndex, 1);
    },
    openAccountDialog(entryIndex, lineIndex) {
      this.accountTarget = { entryIndex, lineIndex };
      this.accountDialogKey++;
      this.showAccountDialog = true;
    },
    receiveAccountSelection(account) {
      if (!this.accountTarget || !account) return;

      const entry = this.entries[this.accountTarget.entryIndex];
      const line = entry && entry.detalle[this.accountTarget.lineIndex];
      if (line) {
        line.idAccount = account.id_account;
        line.cuenta = account.account_code;
        line.nombre = account.account_description;
      }

      this.showAccountDialog = false;
      this.accountTarget = null;
    },
    formatLineAmount(line, field) {
      line[field] = this.toAmount(line[field]);
    },
    getEntryTotals(entry) {
      const totalDebe = entry.detalle.reduce((sum, line) => sum + this.moneyToNumber(line.debe), 0);
      const totalHaber = entry.detalle.reduce((sum, line) => sum + this.moneyToNumber(line.haber), 0);
      const diferencia = this.roundMoney(totalDebe - totalHaber);

      return {
        totalDebe: this.roundMoney(totalDebe),
        totalHaber: this.roundMoney(totalHaber),
        diferencia,
      };
    },
    moneyToNumber(value) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    roundMoney(value) {
      return Math.round((value + Number.EPSILON) * 100) / 100;
    },
    formatMoney(value) {
      return this.roundMoney(value).toFixed(2);
    },
    isEntryBalanced(entry) {
      return this.getEntryTotals(entry).diferencia === 0;
    },
    shouldBlockUnbalancedEntries() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return parseInt(user.nosaveunadjusteditems || 0) === 1;
    },
    isLineAccountInvalid(line) {
      return !line.cuenta || !line.idAccount;
    },
    isLineAmountInvalid(line) {
      return this.moneyToNumber(line.debe) <= 0 && this.moneyToNumber(line.haber) <= 0;
    },
    isLineDoubleAmountInvalid(line) {
      return this.moneyToNumber(line.debe) > 0 && this.moneyToNumber(line.haber) > 0;
    },
    isLineValid(line) {
      return !!line.idAccount
        && !this.isLineAmountInvalid(line)
        && !this.isLineDoubleAmountInvalid(line);
    },
    getEntryErrors(entry) {
      const totals = this.getEntryTotals(entry);
      const errors = [];

      if (!entry.fecha) errors.push("Falta fecha.");
      if (!entry.tipoId) errors.push("Falta tipo de partida.");
      if (!entry.conceptoGeneral) errors.push("Falta concepto general.");
      if (!entry.detalle.length) errors.push("Debe agregar al menos una cuenta.");
      if (totals.totalDebe <= 0) errors.push("Total debe debe ser mayor a cero.");
      if (totals.totalHaber <= 0) errors.push("Total haber debe ser mayor a cero.");
      if (this.shouldBlockUnbalancedEntries() && !this.isEntryBalanced(entry)) {
        errors.push("No se permite guardar partidas descuadradas por configuracion.");
      }
      if (entry.detalle.some((line) => !this.isLineValid(line))) errors.push("Existen lineas con datos invalidos.");

      return errors;
    },
    isEntryValid(entry) {
      return this.getEntryErrors(entry).length === 0;
    },
    getEntryStatusLabel(entry) {
      if (entry.saved) return "Guardada";
      if (!this.isEntryValid(entry)) return "Con errores";
      if (!this.isEntryBalanced(entry)) return "Descuadrada";
      return "Lista";
    },
    getEntryStatusColor(entry) {
      if (entry.saved) return "grey";
      if (!this.isEntryValid(entry)) return "error";
      if (!this.isEntryBalanced(entry)) return "warning";
      return "success";
    },
    selectAllEntries() {
      this.entries.forEach((entry) => {
        entry.selected = !entry.saved && this.isEntryValid(entry);
      });
    },
    unselectAllEntries() {
      this.entries.forEach((entry) => {
        entry.selected = false;
      });
    },
    buildPayload(entry) {
      const deberType = this.detailTypes.find((type) => type.entry_detail_type_description === "DEBER");
      const haberType = this.detailTypes.find((type) => type.entry_detail_type_description === "HABER");
      const totals = this.getEntryTotals(entry);

      return {
        accountingentry: {
          ID_Accounting_item: null,
          id_accounting_entry_type: entry.tipoId,
          accounting_entry_date: new Date(`${entry.fecha}T00:00:00`),
          accounting_entry_comment: entry.conceptoGeneral,
          accounting_entry_value: totals.totalDebe,
          accountingentrydetails: entry.detalle.map((line) => {
            const debe = this.moneyToNumber(line.debe);
            const haber = this.moneyToNumber(line.haber);

            return {
              id_account: line.idAccount,
              id_entry_detail_type: debe > 0 ? deberType.id_entry_detail_type : haberType.id_entry_detail_type,
              amount: debe > 0 ? debe : haber,
              concept: line.concepto || "",
            };
          }),
        },
      };
    },
    async importSelectedEntries() {
      const selectedEntries = this.entries.filter((entry) => entry.selected && !entry.saved);

      if (!selectedEntries.length) {
        this.showAlert("warning", "Sin seleccion", "Seleccione al menos una partida para importar.");
        return;
      }

      const invalidEntries = selectedEntries.filter((entry) => !this.isEntryValid(entry));
      if (invalidEntries.length) {
        this.showAlert("warning", "Partidas invalidas", "Solo se importaran partidas seleccionadas con los campos minimos completos.");
      }

      const validEntries = selectedEntries.filter((entry) => this.isEntryValid(entry));
      if (!validEntries.length) return;

      this.loading = true;
      this.importResults = [];

      for (const entry of validEntries) {
        try {
          await this.$API.accountingentryheader.createAccountingEntryHeader(this.buildPayload(entry));
          entry.selected = false;
          entry.saved = true;
          this.importResults.push({
            localId: entry.localId,
            numero: entry.numero,
            success: true,
            message: "Creada correctamente",
          });
        } catch (error) {
          this.importResults.push({
            localId: entry.localId,
            numero: entry.numero,
            success: false,
            message: this.getErrorMessage(error),
          });
        }
      }

      this.loading = false;
      this.showAlert("success", "Importacion finalizada", "Revise el resultado de cada partida en pantalla.");
    },
    async downloadTemplate() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Plantilla");
      const typesSheet = workbook.addWorksheet("Tipo de partida");
      const instructionsSheet = workbook.addWorksheet("Instrucciones");
      const instructions = [
        ["Instrucciones"],
        ["La columna Tipo debe contener el codigo tipo de partida que se encuentra en la pestaña Tipo de partida, por ejemplo DIA."],
        ["La primera fila de cada partida debe tener Numero, Fecha, Tipo y Concepto, sin Cuenta."],
        ["Las filas siguientes son el detalle de la partida contable."],
      ];

      worksheet.addRow(TEMPLATE_HEADERS);
      worksheet.addRow(["1", "2026-05-16", "", "", "", "", "", ""]);
      worksheet.columns = [
        { width: 12 },
        { width: 14 },
        { width: 14 },
        { width: 18 },
        { width: 28 },
        { width: 36 },
        { width: 14 },
        { width: 14 },
      ];
      worksheet.getRow(1).font = { bold: true };

      typesSheet.addRow(["Codigo", "Nombre"]);
      this.entryTypes.forEach((type) => {
        typesSheet.addRow([
          type.accounting_entry_code || "",
          type.accounting_entry_name || "",
        ]);
      });
      typesSheet.columns = [
        { width: 18 },
        { width: 36 },
      ];
      typesSheet.getRow(1).font = { bold: true };

      instructions.forEach((row) => instructionsSheet.addRow(row));
      instructionsSheet.columns = [{ width: 120 }];
      instructionsSheet.getRow(1).font = { bold: true };

      const lastTypeRow = Math.max(this.entryTypes.length + 1, 2);
      for (let rowIndex = 2; rowIndex <= 500; rowIndex++) {
        worksheet.getCell(`C${rowIndex}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`'Tipo de partida'!$A$2:$A$${lastTypeRow}`],
          showErrorMessage: true,
          errorTitle: "Tipo invalido",
          error: "Seleccione un codigo de la pestaña Tipo de partida.",
        };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        "Plantilla_Importacion_Partidas.xlsx"
      );
    },
    getErrorMessage(error) {
      if (!error) return "Error desconocido";
      if (error.response && error.response.data && error.response.data.message) return error.response.data.message;
      if (error.message) return error.message;
      if (error.body) return error.body;
      if (typeof error === "string") return error;
      return "Error desconocido";
    },
    showAlert(type, header, body, options = null, data = null) {
      this.alert.type = type || "danger";
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
      this.alert.options = options;
      this.alert.data = data;
    },
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
  },
};
</script>

<style scoped>
.entry-card {
  border-width: 1px;
}

.entry-card-error {
  border-color: #e53935 !important;
}

.entry-card-warning {
  border-color: #fb8c00 !important;
}

.entry-card-ready {
  border-color: #43a047 !important;
}

.entry-card-title {
  align-items: center;
  gap: 4px;
}

.entry-title-text {
  font-size: 1rem;
  font-weight: 600;
}

.entry-lines-wrapper {
  overflow-x: auto;
}

.entry-lines-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
}

.entry-lines-table th,
.entry-lines-table td {
  padding: 6px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
}

.entry-lines-table th {
  text-align: left;
  font-weight: 600;
  background: #f7f7f7;
}

.account-cell {
  width: 210px;
}

.line-input {
  min-width: 160px;
}

.amount-input {
  width: 130px;
}

.result-line {
  line-height: 1.5;
}
</style>
